import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiTv, FiFilm, FiLayers, FiCompass } from 'react-icons/fi';
import { API_CONFIG } from '../utils/api';
import '../styles/live-schedule.css';

const TIMEZONE = 'America/Sao_Paulo';
const REFRESH_MS = 60 * 1000;

const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIMEZONE,
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
});

const formatTime = (iso) => timeFormatter.format(new Date(iso));
const formatDay = (key) => dateFormatter.format(new Date(`${key}T12:00:00-03:00`)).replace('.', '');

const isDateKey = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || '');
const sharedDateFromLocation = () => {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('date');
  return isDateKey(value) ? value : null;
};

const progressOf = (program, now) => {
  const start = Date.parse(program.start);
  const end = Date.parse(program.end);
  return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  const data = await response.json().catch(() => null);
  if (!response.ok && !data) throw new Error('Falha ao carregar a programação.');
  return { ok: response.ok, data };
};

// Capa do programa, quando o catálogo tem; sem capa (ou se a imagem falhar), o card fica só com o texto.
const Artwork = ({ program, variant }) => {
  const [failed, setFailed] = useState(false);
  if (!program.posterUrl || failed) return null;
  return (
    <img
      className={`epg-artwork epg-artwork--${variant}`}
      src={program.posterUrl}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const ChannelCard = ({ channel, now, onOpen }) => {
  const [showSynopsis, setShowSynopsis] = useState(false);

  if (!channel.available) {
    return (
      <article className="epg-card epg-card--empty">
        <h3 className="epg-channel">{channel.channel}</h3>
        <p className="epg-unavailable">Programação indisponível no momento.</p>
      </article>
    );
  }

  return (
    <article className="epg-card">
      <h3 className="epg-channel">{channel.channel}</h3>

      <div className="epg-now">
        <span className="epg-badge epg-badge--live">
          <i aria-hidden="true" /> AO VIVO AGORA
        </span>
        {channel.current ? (
          <>
            <button
              type="button"
              className="epg-current-trigger"
              aria-expanded={showSynopsis}
              aria-controls={`epg-synopsis-${channel.slug}`}
              aria-label={`${showSynopsis ? 'Ocultar' : 'Ver'} sinopse: ${channel.current.title}`}
              onClick={() => setShowSynopsis((visible) => !visible)}
            >
              <Artwork program={channel.current} variant="current" />
              <span className="epg-title">{channel.current.title}</span>
              <span className="epg-synopsis-toggle">{showSynopsis ? 'Ocultar sinopse' : 'Ver sinopse'}</span>
            </button>
            <p className="epg-time">
              {formatTime(channel.current.start)} – {formatTime(channel.current.end)}
            </p>
            <div className="epg-progress" aria-hidden="true">
              <span style={{ width: `${progressOf(channel.current, now)}%` }} />
            </div>
            {showSynopsis && (
              <p id={`epg-synopsis-${channel.slug}`} className="epg-desc epg-current-synopsis">
                {channel.current.description || 'Sinopse não disponível para este programa.'}
              </p>
            )}
          </>
        ) : (
          <p className="epg-muted">Sem informação do programa atual.</p>
        )}
      </div>

      <div className="epg-next">
        <span className="epg-badge">A SEGUIR</span>
        {channel.next ? (
          <div className="epg-next-content">
            <Artwork program={channel.next} variant="next" />
            <p className="epg-next-line">
              <strong>{formatTime(channel.next.start)}</strong> {channel.next.title}
            </p>
          </div>
        ) : (
          <p className="epg-muted">Sem informação do próximo programa.</p>
        )}
      </div>

      <button type="button" className="epg-link" onClick={() => onOpen(channel)}>
        Ver grade do dia
      </button>
    </article>
  );
};

const DayGuide = ({ channel, channels = [], onNavigate, dates, initialDate, onClose, onShare, shareStatus }) => {
  // Passar para o canal anterior/próximo sem fechar: setas, deslizar com o dedo ou ← →.
  const index = channels.findIndex((c) => c.slug === channel.slug);
  const prev = index > 0 ? channels[index - 1] : null;
  const next = index >= 0 && index < channels.length - 1 ? channels[index + 1] : null;
  const [direction, setDirection] = useState(0);
  const goTo = useCallback((target, dir) => { if (target && onNavigate) { setDirection(dir); onNavigate(target); } }, [onNavigate]);
  const touchRef = useRef(null);
  const onTouchStart = (event) => { if (event.target.closest('.epg-days')) { touchRef.current = null; return; } const t = event.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (event) => {
    const start = touchRef.current; touchRef.current = null;
    if (!start) return;
    const t = event.changedTouches[0];
    const dx = t.clientX - start.x, dy = t.clientY - start.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) goTo(dx < 0 ? next : prev, dx < 0 ? 1 : -1);
  };
  const [date, setDate] = useState(initialDate);
  const [state, setState] = useState({ status: 'loading', programs: [] });
  const [now, setNow] = useState(Date.now());
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const scrollY = window.scrollY;
    const activeElement = document.activeElement;
    const body = document.body;
    const root = document.documentElement;
    const bodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    const rootOverflow = root.style.overflow;
    const rootScrollBehavior = root.style.scrollBehavior;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    root.style.scrollBehavior = 'auto';
    closeButtonRef.current?.focus({ preventScroll: true });

    return () => {
      Object.assign(body.style, bodyStyles);
      root.style.overflow = rootOverflow;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, scrollY);
      root.style.scrollBehavior = rootScrollBehavior;
      if (activeElement instanceof HTMLElement) activeElement.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', programs: [] });
    fetchJson(`${API_CONFIG.PROGRAMACAO.URL}?channel=${channel.slug}&date=${date}`)
      .then(({ ok, data }) => {
        if (!active) return;
        const entry = data && data.channels && data.channels[0];
        if (!ok || !entry) throw new Error('indisponivel');
        // A fonte às vezes repete o mesmo programa no mesmo horário: mostramos uma vez só.
        const seen = new Set();
        const programs = entry.programs.filter((p) => {
          const key = `${p.start}|${p.title}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setState({ status: programs.length ? 'ready' : 'empty', programs });
      })
      .catch(() => active && setState({ status: 'error', programs: [] }));
    return () => { active = false; };
  }, [channel.slug, date]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  // Ao abrir (ou trocar de dia), a lista já começa no que está passando agora, lá em cima.
  // Programas que já passaram continuam acima, a uma rolagem de distância.
  const listRef = useRef(null);
  useEffect(() => {
    if (state.status !== 'ready') return;
    const list = listRef.current;
    const target = list?.querySelector('li.is-live') || list?.querySelector('li:not(.is-past)');
    if (!list || !target) return undefined;
    const content = list.firstElementChild;
    const pin = () => {
      // No fim do dia sobra pouca lista abaixo do programa atual; um espaço no fim deixa ele subir até o topo.
      if (content) {
        content.style.paddingBottom = '0px';
        const below = content.getBoundingClientRect().bottom - target.getBoundingClientRect().top;
        content.style.paddingBottom = `${Math.max(0, list.clientHeight - below - 8)}px`;
      }
      list.scrollTop += target.getBoundingClientRect().top - list.getBoundingClientRect().top - 8;
    };
    pin();
    // As capas acima terminam de carregar depois e empurram a lista: mantém o programa atual
    // no topo até a pessoa mexer na lista (ou por alguns segundos).
    let touched = false;
    const stop = () => { touched = true; };
    const ro = content && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { if (!touched) pin(); }) : null;
    if (ro) ro.observe(content);
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((ev) => list.addEventListener(ev, stop, { passive: true }));
    const timer = setTimeout(() => ro && ro.disconnect(), 4000);
    return () => {
      clearTimeout(timer);
      if (ro) ro.disconnect();
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((ev) => list.removeEventListener(ev, stop));
    };
  }, [state.status, date]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'ArrowRight' && next) { goTo(next, 1); return; }
      if (event.key === 'ArrowLeft' && prev) { goTo(prev, -1); return; }

      if (event.key !== 'Tab') return;
      const modal = document.querySelector('.epg-modal');
      const focusable = modal && modal.querySelectorAll('button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])');
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, goTo, next, prev]);

  return createPortal(
    <div className="epg-overlay" role="dialog" aria-modal="true" aria-label={`Grade do dia: ${channel.channel}`} onClick={onClose}>
      <div className="epg-modal" onClick={(event) => event.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <header className="epg-modal-header">
          <div className="epg-modal-heading">
            {channels.length > 1 && (
              <div className="epg-channel-nav">
                <button type="button" className="epg-pager-btn" onClick={() => goTo(prev, -1)} disabled={!prev} aria-label={prev ? `Canal anterior: ${prev.channel}` : 'Sem canal anterior'}>‹</button>
                <span className="epg-channel-count">{index + 1} de {channels.length}</span>
                <button type="button" className="epg-pager-btn" onClick={() => goTo(next, 1)} disabled={!next} aria-label={next ? `Próximo canal: ${next.channel}` : 'Sem próximo canal'}>›</button>
              </div>
            )}
            <h3 key={channel.slug} className={`epg-guide-title${direction > 0 ? ' from-right' : direction < 0 ? ' from-left' : ''}`}>{channel.channel}</h3>
            <button type="button" className="epg-share epg-share--modal" onClick={() => onShare(date)}>
              Compartilhar este dia
            </button>
            {shareStatus && <span className="epg-share-status" role="status">{shareStatus}</span>}
          </div>
          <button ref={closeButtonRef} type="button" className="epg-close" onClick={onClose} aria-label="Fechar grade">×</button>
        </header>

        <div className="epg-days" role="tablist">
          {dates.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={key === date}
              className={`epg-day${key === date ? ' is-active' : ''}`}
              onClick={() => setDate(key)}
            >
              {formatDay(key)}
            </button>
          ))}
        </div>

        <div className="epg-list" aria-label="Programação do canal" ref={listRef}>
          {state.status === 'loading' && <p className="epg-muted">Carregando grade...</p>}
          {state.status === 'error' && <p className="epg-error">Não foi possível carregar a grade deste dia.</p>}
          {state.status === 'empty' && <p className="epg-muted">Sem programação disponível para este dia.</p>}
          {state.status === 'ready' && (
            <ul>
              {state.programs.map((program) => {
                const live = Date.parse(program.start) <= now && now < Date.parse(program.end);
                const past = Date.parse(program.end) <= now;
                return (
                  <li key={`${program.start}-${program.title}`} className={live ? 'is-live' : past ? 'is-past' : ''}>
                    <time>{formatTime(program.start)}</time>
                    <div className="epg-guide-entry">
                      <Artwork program={program} variant="guide" />
                      <div className="epg-guide-details">
                        <p className="epg-title">
                        {program.title}
                        {live && <span className="epg-badge epg-badge--live epg-badge--inline">AO VIVO</span>}
                      </p>
                      <p className="epg-time">
                        até {formatTime(program.end)} · {program.duration} min
                        {program.rating ? ` · ${program.rating}` : ''}
                      </p>
                        {program.description && <p className="epg-desc">{program.description}</p>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Abas da programação: canais abertos e de esporte juntos em "TV ao vivo".
const FILTERS = [
  { key: 'aovivo', label: 'TV ao vivo', Icon: FiTv, groups: ['abertos', 'esportes'] },
  { key: 'filmes', label: 'Filmes', Icon: FiFilm, groups: ['filmes'] },
  { key: 'series', label: 'Séries', Icon: FiLayers, groups: ['series'] },
  { key: 'variedades', label: 'Variedades', Icon: FiCompass, groups: ['documentarios'] },
];

const LiveSchedule = () => {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [group, setGroup] = useState('aovivo');
  const [now, setNow] = useState(Date.now());
  const [guide, setGuide] = useState(null);
  // No celular a grade vira um carrossel lateral: guardamos qual card está à vista.
  const gridRef = useRef(null);
  const [slide, setSlide] = useState(0);
  const onGridScroll = useCallback(() => {
    const el = gridRef.current;
    const first = el?.firstElementChild;
    if (!el || !first) return;
    const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(el).columnGap || '0');
    setSlide(Math.round(el.scrollLeft / Math.max(1, step)));
  }, []);
  const goSlide = useCallback((index) => {
    const el = gridRef.current;
    const card = el?.children[index];
    if (!el || !card) return;
    el.scrollTo({ left: card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2, behavior: 'smooth' });
  }, []);
  const [shareStatus, setShareStatus] = useState('');
  const sharedDate = sharedDateFromLocation();

  const load = useCallback(async () => {
    try {
      const requestUrl = new URL(API_CONFIG.PROGRAMACAO.URL, window.location.origin);
      if (sharedDate) requestUrl.searchParams.set('date', sharedDate);
      const { ok, data } = await fetchJson(requestUrl.toString());
      if (!ok || !data || !Array.isArray(data.channels)) throw new Error('indisponivel');
      setState({ status: 'ready', data });
      setNow(Date.now());
    } catch {
      setState((previous) => (previous.data ? previous : { status: 'error', data: null }));
    }
  }, [sharedDate]);

  const copyScheduleLink = useCallback(async (date) => {
    const url = new URL(window.location.href);
    url.searchParams.set('date', date);
    url.hash = 'programacao';
    try {
      await navigator.clipboard.writeText(url.toString());
      setShareStatus('Link copiado!');
    } catch {
      window.prompt('Copie o link da programacao:', url.toString());
    }
    window.setTimeout(() => setShareStatus(''), 2500);
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!sharedDate || state.status !== 'ready') return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById('programacao')?.scrollIntoView({ block: 'start' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [sharedDate, state.status]);

  const { data } = state;
  const available = useMemo(
    () => (data ? data.channels.filter((channel) => channel.available) : []),
    [data],
  );
  const filters = useMemo(
    () => FILTERS.filter((item) => available.some((channel) => item.groups.includes(channel.group))),
    [available],
  );
  const visible = useMemo(() => {
    if (!data) return [];
    const active = FILTERS.find((item) => item.key === group) || FILTERS[0];
    return data.channels.filter((channel) => active.groups.includes(channel.group));
  }, [data, group]);
  const missing = data ? data.channels.filter((channel) => !channel.available) : [];

  return (
    <section id="programacao" className="relative px-4 py-12 text-white sm:py-16">
      <div className="container mx-auto">
        <div className="agtv-section-heading">
          <span className="agtv-section-kicker">PROGRAMAÇÃO DE TV</span>
          <h2 className="agtv-section-title">
            No ar <span>agora</span>
          </h2>
        </div>
        <p className="agtv-section-description mb-8">
          Veja o que está passando e o que vem a seguir nos principais canais. Horários de Brasília.
        </p>

        {state.status === 'loading' && (
          <div className="epg-grid" aria-busy="true" aria-live="polite">
            {Array.from({ length: 6 }, (_, index) => <div key={index} className="epg-card epg-skeleton" />)}
          </div>
        )}

        {state.status === 'error' && (
          <p className="epg-state epg-error" role="alert">
            Não foi possível carregar a programação agora. Tente novamente em instantes.
            <button type="button" className="epg-link" onClick={() => { setState({ status: 'loading', data: null }); load(); }}>
              Tentar novamente
            </button>
          </p>
        )}

        {state.status === 'ready' && available.length === 0 && (
          <p className="epg-state" role="status">
            A programação dos canais não está disponível no momento. Ela volta a aparecer assim que as fontes forem atualizadas.
          </p>
        )}

        {state.status === 'ready' && available.length > 0 && (
          <>
            <div className="epg-filters-sticky">
              <div className="epg-filters" role="tablist" aria-label="Categorias de canais">
                {filters.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={group === key}
                    className={`epg-filter${group === key ? ' is-active' : ''}`}
                    onClick={() => setGroup(key)}
                  >
                    <Icon aria-hidden="true" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="epg-grid" ref={gridRef} onScroll={onGridScroll}>
              {visible.map((channel) => (
                <ChannelCard key={channel.slug} channel={channel} now={now} onOpen={setGuide} />
              ))}
            </div>

            {visible.length > 1 && (
              <div className="epg-pager" aria-label="Navegar entre os canais">
                <button type="button" className="epg-pager-btn" onClick={() => goSlide(Math.max(0, slide - 1))} disabled={slide === 0} aria-label="Canal anterior">‹</button>
                <span className="epg-pager-count" aria-live="polite">
                  {Math.min(slide + 1, visible.length)} de {visible.length} canais
                </span>
                <button type="button" className="epg-pager-btn" onClick={() => goSlide(Math.min(visible.length - 1, slide + 1))} disabled={slide >= visible.length - 1} aria-label="Próximo canal">›</button>
              </div>
            )}

            {missing.length > 0 && (
              <p className="epg-footnote">
                Sem dados de programação no momento: {missing.map((channel) => channel.channel).join(', ')}.
              </p>
            )}
          </>
        )}
      </div>

      {guide && data && (
        <DayGuide
          channel={guide}
          channels={visible.filter((c) => c.available !== false)}
          onNavigate={setGuide}
          dates={Array.from(new Set([
            ...(sharedDate ? [sharedDate] : []),
            ...(data.availableDates && data.availableDates.length ? data.availableDates : [data.date]),
          ]))}
          initialDate={sharedDate || data.date}
          onClose={() => setGuide(null)}
          onShare={copyScheduleLink}
          shareStatus={shareStatus}
        />
      )}
    </section>
  );
};

export default LiveSchedule;
